import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Users, UserCheck, DollarSign, TrendingUp, Smartphone, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSettings } from '../contexts/SettingsContext';

// Configurações de variáveis de ambiente
const API_URL = process.env.REACT_APP_API_URL || '/api';
const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10);
const MAX_RETRIES = parseInt(process.env.REACT_APP_MAX_RETRIES || '3', 10);
const RETRY_DELAY = parseInt(process.env.REACT_APP_RETRY_DELAY || '1000', 10);
const WHATSAPP_API_URL = process.env.REACT_APP_WHATSAPP_API_URL || '/api/whatsapp';
const DASHBOARD_REFRESH_INTERVAL = parseInt(process.env.REACT_APP_DASHBOARD_REFRESH_INTERVAL || '5000', 10);
const ENABLE_ERROR_LOGGING = process.env.REACT_APP_ENABLE_ERROR_LOGGING === 'true';

const Dashboard = () => {
  const { darkMode } = useSettings();
  const [stats, setStats] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [conversionRate, setConversionRate] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  const [whatsappType, setWhatsappType] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [apiError, setApiError] = useState(false);

  // Função auxiliar para retry com timeout
  const fetchWithRetry = useCallback(async (url, options = {}, retries = MAX_RETRIES) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);

      if (ENABLE_ERROR_LOGGING) {
        console.error(`Erro na requisição (${retries} tentativas restantes):`, error);
      }

      if (retries > 0) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchWithRetry(url, options, retries - 1);
      }

      throw error;
    }
  }, []);

  const fetchWhatsAppStatus = useCallback(async () => {
    try {
      const data = await fetchWithRetry(`${WHATSAPP_API_URL}/status`);
      setWhatsappConnected(data.connected);
      setWhatsappType(data.type || null);
      setWhatsappMessage(data.message || '');
    } catch (err) {
      if (ENABLE_ERROR_LOGGING) {
        console.error('Erro ao verificar status do WhatsApp:', err);
      }
    }
  }, [fetchWithRetry]);

  useEffect(() => {
    fetchDashboardData();
    fetchWhatsAppStatus();

    // Atualizar status do WhatsApp usando variável de ambiente
    const interval = setInterval(fetchWhatsAppStatus, DASHBOARD_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchWhatsAppStatus]);

  const fetchDashboardData = async () => {
    try {
      // Configurar axios com timeout
      const axiosConfig = {
        timeout: API_TIMEOUT
      };

      const [statsRes, leadsRes, conversionRes, revenueRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`, axiosConfig),
        axios.get(`${API_URL}/dashboard/recent-leads?limit=5`, axiosConfig),
        axios.get(`${API_URL}/dashboard/conversion-rate`, axiosConfig),
        axios.get(`${API_URL}/dashboard/revenue?period=30`, axiosConfig)
      ]);

      // Validar dados antes de definir estado
      setStats(statsRes.data || null);
      setRecentLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
      setConversionRate(conversionRes.data || null);
      setRevenue(revenueRes.data || null);
      setApiError(false);
    } catch (error) {
      if (ENABLE_ERROR_LOGGING) {
        console.error('Erro ao buscar dados do dashboard:', error);
      }

      setStats(null);
      setRecentLeads([]);
      setConversionRate(null);
      setRevenue(null);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-500">Carregando dashboard...</div>
        </div>
      </div>
    );
  }

  // Validar e formatar dados dos cards
  const statCards = [
    {
      title: 'Total de Leads',
      value: stats?.total_leads ?? 0,
      icon: Users,
      color: 'bg-blue-500',
      detail: `${stats?.novos_30_dias ?? 0} nos últimos 30 dias`
    },
    {
      title: 'Contratos Assinados',
      value: stats?.contratos_assinados ?? 0,
      icon: UserCheck,
      color: 'bg-green-500',
      detail: 'Assinaturas confirmadas'
    },
    {
      title: 'Processos Concluídos',
      value: stats?.concluidos ?? 0,
      icon: TrendingUp,
      color: 'bg-purple-500',
      detail: 'Nada consta concluído'
    },
    {
      title: 'Receita Total',
      value: Number(revenue?.total_receita || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }),
      icon: DollarSign,
      color: 'bg-yellow-500',
      detail: 'Somente pagamentos aprovados'
    }
  ];

  return (
    <div className="space-y-6">
      {/* API Error Warning */}
      {apiError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
          <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          <span className="font-medium text-yellow-800 dark:text-yellow-300">
          </span>
        </div>
      )}

      <div>
        <h1 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Dashboard</h1>
        <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Visão geral do seu negócio</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={darkMode ? 'text-gray-400 text-sm' : 'text-gray-500 text-sm'}>{stat.title}</p>
                  <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <Icon className="text-white" size={24} />
                </div>
              </div>
              <div
                className={
                  darkMode
                    ? 'mt-4 text-sm text-gray-400'
                    : 'mt-4 text-sm text-gray-500'
                }
              >
                {stat.detail}
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Receita dos Últimos 30 Dias</h2>
          {revenue?.por_dia && Array.isArray(revenue.por_dia) && revenue.por_dia.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenue.por_dia}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#374151' : '#e5e7eb'} />
                <XAxis dataKey="data" stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <YAxis stroke={darkMode ? '#9ca3af' : '#6b7280'} />
                <Tooltip contentStyle={{ backgroundColor: darkMode ? '#1f2937' : '#ffffff', color: darkMode ? '#ffffff' : '#000000' }} />
                <Bar dataKey="receita" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 mx-auto mb-2" />
                <p>Sem dados disponíveis</p>
              </div>
            </div>
          )}
        </div>

        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
          <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Taxa de Conversão</h2>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-5xl font-bold text-blue-600">
                {typeof conversionRate?.taxa_conversao === 'number' ? conversionRate.taxa_conversao.toFixed(2) : '0.00'}%
              </p>
              <p className={darkMode ? 'text-gray-400 mt-2' : 'text-gray-500 mt-2'}>
                {conversionRate?.convertidos ?? 0} de {conversionRate?.total ?? 0} leads convertidos
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Leads */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm p-6`}>
        <h2 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Leads Recentes</h2>
        {recentLeads && Array.isArray(recentLeads) && recentLeads.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`text-left text-sm border-b ${darkMode ? 'text-gray-400 border-gray-700' : 'text-gray-500 border-gray-200'}`}>
                  <th className="pb-3">Nome</th>
                  <th className="pb-3">CPF/CNPJ</th>
                  <th className="pb-3">Telefone</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Data</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => {
                  const validDate = lead.data_criacao ? new Date(lead.data_criacao) : new Date();
                  return (
                    <tr key={lead.id || lead.telefone || lead.data_criacao} className={`border-b last:border-0 ${darkMode ? 'border-gray-700' : ''}`}>
                      <td className={`py-3 font-medium ${darkMode ? 'text-white' : ''}`}>{lead.nome || 'N/A'}</td>
                      <td className={`py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{lead.cpf_cnpj || 'N/A'}</td>
                      <td className={`py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{lead.telefone || 'N/A'}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          lead.status === 'concluido' ? 'bg-green-100 text-green-800' :
                          lead.status === 'contrato_assinado' ? 'bg-blue-100 text-blue-800' :
                          lead.status === 'pago' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {lead.status || 'Desconhecido'}
                        </span>
                      </td>
                      <td className="py-3 text-gray-600">
                        {validDate.toLocaleDateString('pt-BR')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <AlertCircle className="w-8 h-8 mx-auto mb-2" />
              <p>Nenhum lead encontrado</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
